(() => {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const markMediaLoaded = (root = document) => {
    root.querySelectorAll('.axn-media img').forEach((image) => {
      if (image.complete) image.classList.add('is-loaded');
      else image.addEventListener('load', () => image.classList.add('is-loaded'), { once: true });
    });

    root.querySelectorAll('.axn-media video').forEach((video) => {
      if (video.readyState >= 2) video.classList.add('is-loaded');
      else video.addEventListener('loadeddata', () => video.classList.add('is-loaded'), { once: true });
    });
  };

  const initReveal = (root = document) => {
    const items = [...root.querySelectorAll('[data-axisray-reveal]')].filter((item) => !item.dataset.axisrayRevealReady);
    if (!items.length) return;

    items.forEach((item) => { item.dataset.axisrayRevealReady = 'true'; });
    if (reducedMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach((item) => item.classList.add('is-axisray-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-axisray-visible');
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.12 });

    items.forEach((item) => observer.observe(item));
  };

  const initMediaPlayback = (root = document) => {
    const videos = [...root.querySelectorAll('.axn-media video')].filter((video) => !video.dataset.axisrayPlaybackReady);
    if (!videos.length) return;
    videos.forEach((video) => { video.dataset.axisrayPlaybackReady = 'true'; });

    if (!('IntersectionObserver' in window)) {
      videos.forEach((video) => video.play().catch(() => {}));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) video.play().catch(() => {});
        else video.pause();
      });
    }, { rootMargin: '120px 0px', threshold: 0.08 });

    videos.forEach((video) => observer.observe(video));
  };

  const initRails = (root = document) => {
    const rails = [...root.querySelectorAll('[data-axisray-rail]')].filter((rail) => !rail.dataset.axisrayRailReady);

    rails.forEach((rail) => {
      rail.dataset.axisrayRailReady = 'true';
      rail.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        const firstCard = rail.firstElementChild;
        if (!firstCard) return;
        const styles = getComputedStyle(rail);
        const gap = parseFloat(styles.columnGap || styles.gap) || 16;
        const distance = firstCard.getBoundingClientRect().width + gap;
        event.preventDefault();
        rail.scrollBy({
          left: event.key === 'ArrowRight' ? distance : -distance,
          behavior: reducedMotion.matches ? 'auto' : 'smooth'
        });
      });
    });
  };

  const initComparisons = (root = document) => {
    const comparisons = [...root.querySelectorAll('[data-axisray-comparison]')]
      .filter((comparison) => !comparison.dataset.axisrayComparisonReady);

    comparisons.forEach((comparison) => {
      comparison.dataset.axisrayComparisonReady = 'true';
      const tabs = [...comparison.querySelectorAll('[data-axisray-comparison-tab]')];
      const panels = [...comparison.querySelectorAll('[data-axisray-comparison-panel]')];
      const playback = comparison.querySelector('[data-axisray-comparison-playback]');
      const playbackLabel = comparison.querySelector('[data-axisray-comparison-playback-label]');
      let activeKey = tabs.find((tab) => tab.classList.contains('is-active'))?.dataset.axisrayComparisonTab || tabs[0]?.dataset.axisrayComparisonTab;
      let isPaused = false;
      let isInView = false;
      let isBuffering = false;
      let hasPlaybackError = false;
      let playbackRun = 0;
      let isRestarting = false;

      const activePanel = () => panels.find((panel) => panel.dataset.axisrayComparisonPanel === activeKey);
      const panelVideos = (panel = activePanel()) => panel ? [...panel.querySelectorAll('video')] : [];
      const isActiveVideo = (video) => activePanel()?.contains(video);

      const pauseAll = () => {
        panels.forEach((panel) => panel.querySelectorAll('video').forEach((video) => video.pause()));
      };

      const updatePlayback = () => {
        if (!playback || !playbackLabel) return;
        playback.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
        playback.disabled = hasPlaybackError;

        if (hasPlaybackError) playbackLabel.textContent = playback.dataset.errorLabel;
        else if (isPaused) playbackLabel.textContent = playback.dataset.playLabel;
        else if (isBuffering) playbackLabel.textContent = playback.dataset.loadingLabel;
        else playbackLabel.textContent = playback.dataset.pauseLabel;
      };

      const setBuffering = (value, failed = false) => {
        isBuffering = value;
        hasPlaybackError = failed;
        comparison.classList.toggle('is-buffering', value && !failed);
        comparison.classList.toggle('has-playback-error', failed);
        comparison.setAttribute('aria-busy', value && !failed ? 'true' : 'false');
        updatePlayback();
      };

      const pairReady = (videos) => (
        videos.length === 2 &&
        videos.every((video) => !video.error && video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA)
      );

      const pairTime = (videos) => {
        const times = videos
          .map((video) => Number(video.currentTime))
          .filter((time) => Number.isFinite(time));
        return times.length ? Math.min(...times) : 0;
      };

      const preparePair = (videos) => {
        videos.forEach((video) => {
          video.muted = true;
          video.playsInline = true;
          if (video.dataset.axisrayComparisonPrepared) return;
          video.dataset.axisrayComparisonPrepared = 'true';
          video.preload = 'auto';
          try { video.load(); } catch (_) {}
        });
      };

      const seekVideo = (video, time) => {
        if (Math.abs(video.currentTime - time) <= .04) return Promise.resolve();

        return new Promise((resolve) => {
          let timer;
          const finish = () => {
            clearTimeout(timer);
            video.removeEventListener('seeked', finish);
            video.removeEventListener('error', finish);
            resolve();
          };

          video.addEventListener('seeked', finish, { once: true });
          video.addEventListener('error', finish, { once: true });
          timer = setTimeout(finish, 1200);

          try { video.currentTime = time; } catch (_) { finish(); }
        });
      };

      const playActive = async ({ reset = false } = {}) => {
        const run = ++playbackRun;
        pauseAll();
        if (!isInView || isPaused) return;

        const videos = panelVideos();
        if (videos.length !== 2 || videos.some((video) => video.error)) {
          setBuffering(true, true);
          return;
        }

        preparePair(videos);
        if (!pairReady(videos)) {
          setBuffering(true);
          return;
        }

        const startTime = reset ? 0 : pairTime(videos);
        setBuffering(false);
        await Promise.all(videos.map((video) => seekVideo(video, startTime)));

        if (run !== playbackRun || !isInView || isPaused || !pairReady(videos)) {
          if (run === playbackRun && isInView && !isPaused) setBuffering(true);
          return;
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));
        const results = await Promise.allSettled(videos.map((video) => video.play()));
        if (run !== playbackRun) return;

        if (results.some((result) => result.status === 'rejected')) {
          videos.forEach((video) => video.pause());
          isPaused = true;
          setBuffering(false);
        }
      };

      const interruptPair = (video, failed = false) => {
        if (!isInView || isPaused || !isActiveVideo(video)) return;
        playbackRun += 1;
        panelVideos().forEach((item) => item.pause());
        setBuffering(true, failed);
        if (!failed) requestAnimationFrame(() => recoverPair(video));
      };

      const recoverPair = (video) => {
        if (!isActiveVideo(video) || hasPlaybackError) return;
        const videos = panelVideos();
        if (!pairReady(videos)) return;

        if (!isInView || isPaused) {
          setBuffering(false);
          return;
        }

        if (isBuffering) playActive();
      };

      const setActive = (key, reset = true) => {
        if (!key || key === activeKey && !reset) return;
        playbackRun += 1;
        pauseAll();
        activeKey = key;
        tabs.forEach((tab) => {
          const selected = tab.dataset.axisrayComparisonTab === key;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', selected ? 'true' : 'false');
          tab.tabIndex = selected ? 0 : -1;
        });
        panels.forEach((panel) => {
          const selected = panel.dataset.axisrayComparisonPanel === key;
          panel.classList.toggle('is-active', selected);
          panel.setAttribute('aria-hidden', selected ? 'false' : 'true');
          if (selected && reset) {
            panel.querySelectorAll('video').forEach((video) => {
              try { video.currentTime = 0; } catch (_) {}
            });
          }
        });
        setBuffering(false);
        playActive({ reset });
      };

      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setActive(tab.dataset.axisrayComparisonTab));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
          event.preventDefault();
          const direction = event.key === 'ArrowRight' ? 1 : -1;
          const next = tabs[(index + direction + tabs.length) % tabs.length];
          next.focus();
          setActive(next.dataset.axisrayComparisonTab);
        });
      });

      playback?.addEventListener('click', () => {
        isPaused = !isPaused;
        updatePlayback();
        playActive();
      });

      panels.forEach((panel) => {
        const videos = panelVideos(panel);
        const leader = videos[0];
        const follower = videos[1];

        videos.forEach((video) => {
          video.addEventListener('waiting', () => interruptPair(video));
          video.addEventListener('stalled', () => interruptPair(video));
          video.addEventListener('error', () => interruptPair(video, true));
          video.addEventListener('loadeddata', () => recoverPair(video));
          video.addEventListener('canplay', () => recoverPair(video));
          video.addEventListener('canplaythrough', () => recoverPair(video));
          video.addEventListener('progress', () => recoverPair(video));
          video.addEventListener('ended', () => {
            if (!isActiveVideo(video) || !isInView || isPaused || isRestarting) return;
            isRestarting = true;
            Promise.resolve(playActive({ reset: true })).finally(() => {
              isRestarting = false;
            });
          });
        });

        if (!leader || !follower) return;
        leader.addEventListener('timeupdate', () => {
          if (
            panel !== activePanel() ||
            isPaused ||
            isBuffering ||
            !pairReady(videos) ||
            Math.abs(leader.currentTime - follower.currentTime) <= .08
          ) return;

          try { follower.currentTime = leader.currentTime; } catch (_) {}
        });
      });

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            isInView = entry.isIntersecting;
            if (isInView) playActive();
            else {
              playbackRun += 1;
              pauseAll();
              setBuffering(false);
            }
          });
        }, { rootMargin: '80px 0px', threshold: .2 });
        observer.observe(comparison);
      } else {
        isInView = true;
        playActive();
      }

      updatePlayback();
      setActive(activeKey, false);
    });
  };

  const init = (root = document) => {
    document.documentElement.classList.add('axisray-narrative-ready');
    markMediaLoaded(root);
    initReveal(root);
    initMediaPlayback(root);
    initRails(root);
    initComparisons(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
